import { Notification } from "@/utils/notificationUtils";
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { notificationsApi } from "../../../modules/notifications/api";
import { NotificationTopic } from "../../../modules/notifications/enums";
import {
  MarkNotificationsAsSeenRequest,
  NotificationFilterPageRequest,
  NotificationSettings,
  UpdateNotificationSettings,
} from "../../../modules/notifications/types";
import { PageSizes } from "../../constants";
import { EntityIdObject } from "../../types";

export const NOTIFICATIONS_QUERY_KEYS = {
  getNotificationSettings: () => ["getNotificationSettings"],
  updateNotificationSettings: () => ["updateNotificationSettings"],
  notificationBadge: () => ["notificationBadge"],
  headerNotifications: () => ["headerNotifications"],
  filterNotifications: (filter?: NotificationFilterPageRequest) =>
    filter ? ["filterNotifications", filter] : ["filterNotifications"],
  markAllNotificationsAsSeen: () => ["markAllNotificationsAsSeen"],
  markNotificationsAsSeen: () => ["markNotificationsAsSeen"],
  toggleNotificationSentAndSeenStatus: () => ["toggleNotificationSentAndSeenStatus"],
};

const invalidateNotificationQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: NOTIFICATIONS_QUERY_KEYS.notificationBadge(),
  });

  queryClient.invalidateQueries({
    queryKey: NOTIFICATIONS_QUERY_KEYS.headerNotifications(),
  });

  queryClient.invalidateQueries({
    queryKey: NOTIFICATIONS_QUERY_KEYS.filterNotifications(),
  });
};

export const useGetNotificationSettings = () => {
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.getNotificationSettings(),
    queryFn: async () => {
      const { data } = await notificationsApi.getNotificationSettings();

      return data;
    },
    select: (data) =>
      [...data].sort(
        (a, b) => Object.keys(NotificationTopic).indexOf(a.topic) - Object.keys(NotificationTopic).indexOf(b.topic),
      ),
  });
};

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: NOTIFICATIONS_QUERY_KEYS.updateNotificationSettings(),
    mutationFn: async (payload: UpdateNotificationSettings[]) => {
      const { data } = await notificationsApi.updateNotificationSettings(payload);

      return data;
    },
    onSuccess: (data: NotificationSettings[]) => {
      queryClient.setQueryData(NOTIFICATIONS_QUERY_KEYS.getNotificationSettings(), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useFilterNotifications = (payload: NotificationFilterPageRequest) => {
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.filterNotifications(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await notificationsApi.filterNotifications(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useNotificationBadge = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.notificationBadge(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await notificationsApi.filterNotifications({
        pageIndex: 0,
        pageSize: PageSizes.HUGE,
      });

      return data;
    },
    enabled,
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (query.dataUpdatedAt) {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_QUERY_KEYS.headerNotifications(),
        refetchType: "active",
      });
    }
  }, [query.dataUpdatedAt, queryClient]);

  return query;
};

export const useInfiniteHeaderNotifications = (enabled: boolean) => {
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_QUERY_KEYS.headerNotifications(),
    meta: { globalSpinner: false },
    initialPageParam: 0,
    enabled,
    queryFn: async ({ pageParam }) => {
      const { data } = await notificationsApi.filterNotifications({
        pageIndex: pageParam,
        pageSize: PageSizes.LARGE,
      });

      return data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.isLast ? undefined : lastPage.pageIndex + 1;
    },
  });
};

export const useMarkAllNotificationsAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: NOTIFICATIONS_QUERY_KEYS.markAllNotificationsAsSeen(),
    meta: { globalSpinner: false },
    mutationFn: async () => {
      const { data } = await notificationsApi.markAllNotificationsAsSeen();

      return data;
    },
    onSuccess: () => {
      invalidateNotificationQueries(queryClient);
    },
  });
};

export const useMarkNotificationsAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: NOTIFICATIONS_QUERY_KEYS.markNotificationsAsSeen(),
    mutationFn: async (payload: MarkNotificationsAsSeenRequest) => {
      const { data } = await notificationsApi.markNotificationsAsSeen(payload);

      return data;
    },
    onSuccess: () => {
      invalidateNotificationQueries(queryClient);
    },
  });
};

export const useToggleNotificationSentAndSeenStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: NOTIFICATIONS_QUERY_KEYS.toggleNotificationSentAndSeenStatus(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityIdObject) => {
      const { data } = await notificationsApi.toggleNotificationSentAndSeenStatus(payload);

      return data;
    },
    onSuccess: () => {
      invalidateNotificationQueries(queryClient);
    },
  });
};
